import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Modal, ModalFooter } from "./Modal";
import { Button } from "./Button";

/**
 * Promise-shaped confirmation dialog. Replaces every `if (!confirm(...))` in
 * the app — native `confirm()` looks broken against the dark theme and
 * doesn't read styles, locale, or keyboard focus. Usage:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: "Delete contact?", danger: true }))) return;
 *   // ...do the destructive thing
 *
 * The modal is mounted once at the top of the tree by <ConfirmProvider>;
 * each call replaces the active prompt rather than queueing, since the
 * UX of "two confirms in a row" almost always means a programming error.
 */

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  /** Defaults to "Confirm". Use a verb that matches the action. */
  confirmLabel?: string;
  /** Defaults to "Cancel". */
  cancelLabel?: string;
  /** When true, confirm button uses the danger variant + warning icon. */
  danger?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const Ctx = createContext<ConfirmFn | null>(null);

interface PendingConfirm extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      // If a previous prompt is still open, resolve it as cancelled
      // before showing the new one. Avoids stuck closures.
      setPending((prev) => {
        prev?.resolve(false);
        return { ...options, resolve };
      });
    });
  }, []);

  const close = useCallback(
    (result: boolean) => {
      setPending((prev) => {
        prev?.resolve(result);
        return null;
      });
    },
    []
  );

  // Stable provider value so consumers can put `confirm` in effect deps.
  const ctxValue = useMemo(() => confirm, [confirm]);

  return (
    <Ctx.Provider value={ctxValue}>
      {children}
      <Modal
        open={pending !== null}
        onClose={() => close(false)}
        title={
          pending?.danger ? (
            <span className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-rose-400" />
              <span>{pending.title}</span>
            </span>
          ) : (
            pending?.title ?? ""
          )
        }
        size="sm"
      >
        {pending?.description && (
          <div className="text-sm text-zinc-300">{pending.description}</div>
        )}
        <ModalFooter>
          <Button variant="ghost" onClick={() => close(false)}>
            {pending?.cancelLabel ?? "Cancel"}
          </Button>
          <Button
            variant={pending?.danger ? "danger" : "primary"}
            onClick={() => close(true)}
            autoFocus
          >
            {pending?.confirmLabel ?? "Confirm"}
          </Button>
        </ModalFooter>
      </Modal>
    </Ctx.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}
